/**
 * LiveDocRow — animated row for the document currently being
 * classified by the AI.
 *
 * Displayed as a pinned item at the top of HubList while
 * `stream.phase === "classifying"` and `stream.currentDocName` is
 * set. Unlike `DocRowSkeleton` (which is generic), this row shows
 * real data extracted from the SSE `classifying` event:
 *
 *   - Actual filename (from `classifying.doc_name`)
 *   - Batch position counter ("3 / 12")
 *   - Animated "Understanding…" state label
 *   - Shimmer strip where the confidence bar will appear
 *
 * The row is NOT interactive (no onClick) — the document is still
 * being processed and can't be opened yet.
 *
 * Animations:
 *   - File badge has a slow pulse (same ai-chip-pulse keyframe from
 *     AIReadinessChip, already injected globally).
 *   - Shimmer strip uses the `ssb-shimmer` keyframe from
 *     StreamingStatusBar (also already injected).
 *   - Row has a subtle translateY(−4px) → 0 entrance on mount.
 *
 * The row transitions to a real DocRow when the inventory `done`
 * event arrives and `rows` is refreshed by VariantCShell.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../../tokens";
import { aiColors } from "../tokens";

// ─── Keyframe injection ─────────────────────────────────────────────
// `ai-chip-pulse` and `ssb-shimmer` are already injected by
// AIReadinessChip and StreamingStatusBar respectively. No
// additional injection needed here.

// ─── Styles ────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: "flex",
    alignItems: "stretch",
    paddingLeft: "3px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    backgroundColor: aiColors.annotationBg,
    borderLeft: `2px solid ${aiColors.classifying}`,
    cursor: "default",
    animationName: {
      from: { opacity: 0, transform: "translateY(-4px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    animationDuration: "200ms",
    animationTimingFunction: "ease-out",
  },
  inner: {
    display: "flex",
    alignItems: "center",
    padding: "8px 14px 8px 10px",
    columnGap: "10px",
    flex: 1,
    minWidth: 0,
  },
  fileBadge: {
    width: "26px",
    height: "32px",
    borderRadius: "2px",
    backgroundColor: brandColors.light,
    border: `1px solid ${brandColors.mid}`,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingBottom: "3px",
    fontSize: "7.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: aiColors.classifying,
    flexShrink: 0,
    // Breathing pulse to signal active AI work.
    animationName: "ai-chip-pulse",
    animationDuration: "1.8s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  main: {
    flex: 1,
    minWidth: 0,
  },
  filenameRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "4px",
    marginBottom: "4px",
  },
  filename: {
    flex: 1,
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: fonts.body,
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    padding: "1px 5px",
    borderRadius: "9999px",
    backgroundColor: "var(--brand-wash)",
    border: `1px solid ${aiColors.classifying}33`,
    color: aiColors.classifying,
    flexShrink: 0,
  },
  statusDot: {
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    backgroundColor: aiColors.classifying,
    animationName: "ai-chip-pulse",
    animationDuration: "1.8s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  statusLabel: {
    fontSize: "8px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },
  shimmerBar: {
    height: "2px",
    borderRadius: "9999px",
    width: "100%",
    backgroundSize: "200% 100%",
    backgroundImage: `linear-gradient(
      90deg,
      #EDE9FE 0%,
      ${aiColors.classifying} 45%,
      ${aiColors.classifying} 55%,
      #EDE9FE 100%
    )`,
    animationName: "ssb-shimmer",
    animationDuration: "2s",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
  },
  positionBadge: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    letterSpacing: "0.3px",
    flexShrink: 0,
    paddingLeft: "4px",
  },
});

// ─── Helpers ───────────────────────────────────────────────────────

function fileExtUpper(filename: string): string {
  const idx = filename.lastIndexOf(".");
  if (idx === -1 || idx === filename.length - 1) return "FILE";
  return filename
    .slice(idx + 1)
    .toUpperCase()
    .slice(0, 4);
}

// ─── Component ─────────────────────────────────────────────────────

export interface LiveDocRowProps {
  /** Filename from `classifying.doc_name`. */
  filename: string;
  /** 1-based position from `classifying.index`. */
  index: number | null;
  /** Total docs from `classifying.total`. */
  total: number | null;
}

export const LiveDocRow: React.FC<LiveDocRowProps> = ({ filename, index, total }) => {
  const styles = useStyles();

  return (
    <div className={styles.root} aria-label={`AI classifying: ${filename}`} data-testid="live-doc-row">
      <div className={styles.inner}>
        <div className={styles.fileBadge} aria-hidden="true">
          {fileExtUpper(filename)}
        </div>

        <div className={styles.main}>
          <div className={styles.filenameRow}>
            <span className={styles.filename}>{filename}</span>
            <span className={styles.statusChip} aria-label="AI classifying">
              <span className={styles.statusDot} aria-hidden="true" />
              <span className={styles.statusLabel}>Understanding…</span>
            </span>
          </div>
          <div className={styles.shimmerBar} aria-hidden="true" />
        </div>

        {index !== null && total !== null && (
          <span className={styles.positionBadge} aria-label={`${index} of ${total}`}>
            {index}&thinsp;/&thinsp;{total}
          </span>
        )}
      </div>
    </div>
  );
};

export default LiveDocRow;
